import { saveAs } from 'file-saver';
import { analyticsInterceptorV2 } from './Axios/analyticsInterceptorv2';

// CSV downloads need the auth token, so a plain link won't do: fetch the
// file as a blob and save it. The first request after the server cache
// expires can take up to a minute.
const DOWNLOAD_TIMEOUT_MS = 90 * 1000;

// Blob.text() where available, FileReader otherwise (older browsers).
const blobText = (blob) =>
  typeof blob.text === 'function'
    ? blob.text()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
      });

const readError = async (error) => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await blobText(data));
      return parsed?.error || parsed?.message;
    } catch {
      return null;
    }
  }
  return data?.error || data?.message;
};

// path is relative to /analytics/api, e.g. '/impact/export?view=projects'.
// Pass another axios instance as `client` for files served elsewhere.
export default async function downloadReport(
  path,
  fallbackName,
  { client = analyticsInterceptorV2, params } = {}
) {
  try {
    const response = await client.get(path, {
      params,
      responseType: 'blob',
      timeout: DOWNLOAD_TIMEOUT_MS,
    });
    const header = response.headers?.['content-disposition'] || '';
    const name = /filename="([^"]+)"/.exec(header)?.[1] || fallbackName;
    // file-saver handles browsers that cancel a download when the object
    // URL is revoked straight after the click (Safari, some Firefox).
    saveAs(response.data, name);
  } catch (error) {
    const message = await readError(error);
    throw new Error(
      message ||
        (error?.code === 'ECONNABORTED'
          ? 'The download took too long. Please try again.'
          : `Download failed${
              error?.response?.status ? ` (${error.response.status})` : ''
            }.`)
    );
  }
}
